<template>
  <RtCard
    :mode="mode"
    :icon="RT_CONFIG_SECTION_META.vibration.icon"
    :title="t('rt.tabs.vibration')"
    class="gap-3"
    :loading="loading"
    @update:mode="(v) => emit('update:mode', v)"
  >
    <template #header-title-action> </template>

    <template #header-action>
      <div class="flex items-center gap-3 shrink-0">
        <RtWarningTipsButton
          v-model:open="isOpenTooltips"
          side="left"
          :content="$t('rt.vibration.powerWarning')"
        />
        <div
          class="flex items-center gap-2 shrink-0"
          role="group"
          :aria-label="t('rt.vibration.typeGroup')"
        >
          <Button
            v-for="option in vibrationTypeOptions"
            :key="option.value"
            variant="fill"
            color="normal"
            size="sm"
            shape="corner"
            :active="vibrationFeedbackType === option.value"
            :aria-pressed="vibrationFeedbackType === option.value"
            :disabled="loading || vibrationFeedbackType === undefined"
            :class="
              cn(
                vibrationFeedbackType === option.value &&
                  'bg-brand-blue1 text-white hover:bg-brand-blue1 hover:text-white active:bg-brand-blue1 active:text-white',
              )
            "
            @click="emit('update:vibrationFeedbackType', option.value)"
          >
            {{ option.label }}
          </Button>
        </div>
      </div>
    </template>

    <div
      class="p-6 flex flex-col size-full justify-start items-start gap-10 bg-key rounded-4xl min-w-0"
    >
      <RtModeSelector
        :model-value="mode"
        :disabled="loading || currentValue === undefined"
        class="shrink-0"
        @update:model-value="(v) => emit('update:mode', v)"
      />
      <RtLabeledSliderRow
        :value="currentValue"
        :min="config.min"
        :max="config.max"
        :default-value="config.default"
        :disabled="loading"
        class="min-w-0"
        @commit="onSliderCommit"
        @preview="onPreview"
      >
        <template #input>
          <RtTitleValueInput
            class="bg-neutral-light-default text-black-white hover:bg-neutral-light-hover hover:text-text-glass-hover active:bg-neutral-light-active active:text-black-white focus:bg-neutral-light-active focus:text-black-white"
            :model-value="displayValue"
            :min="config.min"
            :max="config.max"
            :disabled="loading || currentValue === undefined"
            :tooltip="hasDivergedValues ? t('rt.vibration.differentValues') : undefined"
            @commit="onSliderCommit"
          />
        </template>
      </RtLabeledSliderRow>
    </div>
  </RtCard>
</template>

<script setup lang="ts">
  import { computed, ref } from 'vue'
  import { useI18n } from 'vue-i18n'
  import RtCard from '../RtCard.vue'
  import RtLabeledSliderRow from './RtLabeledSliderRow.vue'
  import RtModeSelector from '../RtModeSelector.vue'
  import RtTitleValueInput from '../RtTitleValueInput.vue'
  import { Button, cn } from '@rapoo/element'
  import { RT_CONFIG_SECTION_META, type RtSelectionMode } from '../../types'
  import type { MouseRTValueConfig, RtSide, RtVibrationFeedbackType } from '@rapoo/device-sdk'
  import RtWarningTipsButton from '../RtWarningTipsButton.vue'

  type SideMap<T> = Partial<Record<RtSide, T>>

  const { t } = useI18n()

  const props = defineProps<{
    vibration?: SideMap<number>
    vibrationFeedbackType?: RtVibrationFeedbackType
    config: MouseRTValueConfig
    feedbackTypeConfig: MouseRTValueConfig
    loading?: boolean
    mode: RtSelectionMode
  }>()

  const emit = defineEmits<{
    commit: [
      payload: {
        field: 'vibrationPress'
        mode: RtSelectionMode
        value: number
      },
    ]
    'update:mode': [value: RtSelectionMode]
    'update:vibrationFeedbackType': [value: RtVibrationFeedbackType]
  }>()

  const vibrationTypeOptions = computed(() =>
    (
      [
        {
          value: 2,
          label: t('rt.vibration.types.electromagnetic') + '-' + t('rt.vibration.feedback') + ' 1',
        },
        {
          value: 1,
          label: t('rt.vibration.types.optical') + '-' + t('rt.vibration.feedback') + ' 2',
        },
        {
          value: 0,
          label: t('rt.vibration.types.mechanical') + '-' + t('rt.vibration.feedback') + ' 3',
        },
      ] as const
    ).filter(
      (option) =>
        option.value >= props.feedbackTypeConfig.min &&
        option.value <= props.feedbackTypeConfig.max,
    ),
  )

  const vibrationFeedbackType = computed(() => props.vibrationFeedbackType)

  /**
   * 根据 mode 解析出当前要展示/编辑的单值；undefined 视为 0（slider 会渲染为最小值，不会闪到 undefined）。
   * - 单侧模式：取该侧值
   * - both 模式：以左侧为展示基准（与 TravelCard 行为一致）
   */
  const currentValue = computed<number | undefined>(() => {
    if (props.mode === 'left') return props.vibration?.left
    if (props.mode === 'right') return props.vibration?.right
    return props.vibration?.left
  })

  const leftVibration = computed(() => props.vibration?.left)
  const rightVibration = computed(() => props.vibration?.right)

  const hasDivergedValues = computed(
    () =>
      props.mode === 'both' &&
      leftVibration.value !== undefined &&
      rightVibration.value !== undefined &&
      leftVibration.value !== rightVibration.value,
  )

  const displayValue = computed(() => {
    if (props.mode === 'left') {
      return leftVibration.value === undefined ? '' : String(leftVibration.value)
    }
    if (props.mode === 'right') {
      return rightVibration.value === undefined ? '' : String(rightVibration.value)
    }
    if (hasDivergedValues.value) return '/'
    if (leftVibration.value !== undefined) return String(leftVibration.value)
    if (rightVibration.value !== undefined) return String(rightVibration.value)
    return ''
  })
  const previewValue = ref<number>(currentValue.value ?? 0)
  const onPreview = (value: number) => {
    previewValue.value = value
  }

  // 判断是否是高强度的振动反馈，强制显示提示
  const isStrongValue = computed<boolean>(() => {
    return previewValue.value == props.config.max
  })

  const tooltipOpen = ref(false)

  const isOpenTooltips = computed<boolean>({
    get: () => {
      return isStrongValue.value || tooltipOpen.value
    },
    set: (value: boolean) => {
      tooltipOpen.value = value
    },
  })

  function onSliderCommit(value: number) {
    emit('commit', { field: 'vibrationPress', mode: props.mode, value })
  }
</script>
